/*!
Copyright (C) 2022 Liberty Infrasystems. All rights reserved.
*/

/* eslint-disable no-console, class-methods-use-this, max-classes-per-file, no-await-in-loop */

import { sleep } from './sleep.js';
import { backoff, randomizedBackoff } from './backoff.js';
import { RetryExhaustedException } from './RetryExhaustedException.js';

/**
 * Emits a 'fail' event when the input function returns null, and an 'error' event
 * when the input function throws an error (immediately before rethrowing it).
 * 
 * @param {*} fn function; returns value, or null (will retry), or throws error (will not retry)
 * @param {*} options 
 * @returns a non-null value
 * @throws error if `fn` throws error or if no retries remaining
 */
async function retryIfNull(fn, options = {}) {
    const { /* timeout = 5000, */ maxAttempts = 5, randomizeBackoff = false, backoffOptions = {}, progress = null } = options;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        if (typeof progress === 'function') {
            progress({ status: 'attempt', current: attempt, max: maxAttempts });
        }
        try {
            const result = await fn();
            if (result !== null) {
                if (typeof progress === 'function') {
                    progress({ status: 'success', current: attempt, max: maxAttempts });
                }
                return result;
            }
            if (typeof progress === 'function') {
                progress({ status: 'fail', current: attempt, max: maxAttempts });
            }
            const backoffMultiplier = randomizeBackoff ? randomizedBackoff(attempt - 1, backoffOptions) : backoff(attempt - 1, backoffOptions);
            const backoffUnit = 1000; // 1 second
            const backoffDelay = backoffMultiplier * backoffUnit;
            if (typeof progress === 'function') {
                progress({ status: 'delay', millis: backoffDelay });
            }
            await sleep(backoffDelay);
        } catch (err) {
            if (typeof progress === 'function') {
                progress({ status: 'error', current: attempt, max: maxAttempts });
            }
            throw err;
        }
    }
    // console.error(`callWithRetry ${config.method} max attempts reached for url: ${config.url}`);
    if (typeof progress === 'function') {
        progress({ status: 'exhausted', max: maxAttempts });
    }
    throw new RetryExhaustedException({ maxAttempts });
}

/**
 * Because this function catches errors, the progress event when an error
 * occurs is 'fail', and it never emits an 'error' event.
 * 
 * @param {*} fn function; returns value, or null (will not retry), or throws error (will retry)
 * @param {*} options 
 * @returns a value or null
 * @throws error if no retries remaining
 */
async function retryIfError(fn, options = {}) {
    const { /* timeout = 5000, */ maxAttempts = 5, randomizeBackoff = false, backoffOptions = {}, progress = null } = options;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        if (typeof progress === 'function') {
            progress({ status: 'attempt', current: attempt, max: maxAttempts });
        }
        try {
            const result = await fn();
            if (typeof progress === 'function') {
                progress({ status: 'success', current: attempt, max: maxAttempts });
            }
            return result;
        } catch (err) {
            if (typeof progress === 'function') {
                progress({ status: 'fail', current: attempt, max: maxAttempts });
            }
        }
        
        const backoffMultiplier = randomizeBackoff ? randomizedBackoff(attempt - 1, backoffOptions) : backoff(attempt - 1, backoffOptions);
        const backoffUnit = 1000; // 1 second
        const backoffDelay = backoffMultiplier * backoffUnit;
        if (typeof progress === 'function') {
            progress({ status: 'delay', millis: backoffDelay });
        }
        await sleep(backoffDelay);
    }
    if (typeof progress === 'function') {
        progress({ status: 'exhausted', max: maxAttempts });
    }
    throw new RetryExhaustedException({ maxAttempts });
}

export { retryIfNull, retryIfError };
